import React from 'react';
import { Routes, Route, Navigate, BrowserRouter, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { SearchProvider } from './contexts/SearchContext';
import { ToastProvider } from './components/ui/Toast';
import AppLayout from './layouts/AppLayout';
import RoleGuard from './components/guards/RoleGuard';
import Login from './pages/Auth/Login';
import Signup from './pages/Auth/Signup';
import ForgotPassword from './pages/Auth/ForgotPassword';
import ResetPassword from './pages/Auth/ResetPassword';
import AcceptInvite from './pages/Auth/AcceptInvite';
import ConfirmEmail from './pages/Auth/ConfirmEmail';
import EmailConfirmationRequired from './pages/Auth/EmailConfirmationRequired';
import SsoHandoff from './pages/Auth/SsoHandoff';
import HomeDashboard from './pages/Pipeline/HomeDashboard';
import AcademyHub from './pages/Pipeline/AcademyHub';
import LaunchToolkit from './pages/Pipeline/LaunchToolkit';
import LaunchPresentationTool from './pages/Pipeline/LaunchPresentationTool';
import Top25List from './pages/Pipeline/Top25List';
import LeadsList from './pages/Leads/LeadsList';
import LeadForm from './pages/Leads/LeadForm';
import LeadDetail from './pages/Leads/LeadDetail';
import Reminders from './pages/Reminders/Reminders';
import Pipeline from './pages/Pipeline/Pipeline';
import Tracker from './pages/Tracker/Tracker';
import CallWorkspace from './pages/Workspace/CallWorkspace';
import Settings from './pages/Settings/Settings';
import Profile from './pages/Profile/Profile';
import Admin from './pages/Admin/Admin';
import Toolkit from './pages/Toolkit/Toolkit';
import ApplicationsList from './pages/Applications/ApplicationsList.jsx';
import ApplicationDetail from './pages/Applications/ApplicationDetail.jsx';
import ApplicationForm from './pages/Applications/ApplicationForm.jsx';
import ApplicationsReports from './pages/Applications/ApplicationsReports.jsx';
import PreUnderwritingLogsList from './pages/Applications/PreUnderwritingLogsList.jsx';
import PreUnderwritingLogDetail from './pages/Applications/PreUnderwritingLogDetail.jsx';
import FinancialAnalysisList from './pages/FinancialAnalysis/FinancialAnalysisList.jsx';
import FinancialAnalysisDetail from './pages/FinancialAnalysis/FinancialAnalysisDetail.jsx';
import FinancialAnalysisReport from './pages/FinancialAnalysis/FinancialAnalysisReport.jsx';
import Inbox from './pages/Inbox/Inbox';
import Calendar from './pages/Calendar/Calendar';
import Campaigns from './pages/Campaigns/Campaigns';
import TemplateManager from './pages/Campaigns/TemplateManager';
import TemplateEditor from './pages/Campaigns/TemplateEditor';
import MarketingIntelligence from './pages/MarketingIntelligence/MarketingIntelligence';
import MarketingIntelligenceForm from './pages/MarketingIntelligence/MarketingIntelligenceForm';
import FunnelList from './pages/Funnels/FunnelList';
import FunnelNew from './pages/Funnels/FunnelNew';
import FunnelBuilder from './pages/Funnels/FunnelBuilder';
import FunnelSettings from './pages/Funnels/FunnelSettings';
import FunnelSubmissions from './pages/Funnels/FunnelSubmissions';
import FunnelTemplates from './pages/Funnels/FunnelTemplates';
import FunnelPage from './pages/Funnel/FunnelPage';
import PreUnderwritingForm from './pages/PreUnderwriting/PreUnderwritingForm';
import EventList from './pages/Mentors/EventList';
import EventEditor from './pages/Mentors/EventEditor';
import EventRegistrations from './pages/Mentors/EventRegistrations';
import PublicBooking from './pages/PublicBooking/PublicBooking';
import StudentApp from './pages/Student/StudentApp';
import Spinner from './components/ui/Spinner';
import { isEmailConfirmationLocked } from './lib/emailConfirmation';

const FUNNEL_WRITE_ROLES = ['manager', 'admin', 'super_admin'];

const fullPageSpinner = (
  <div className="min-h-screen w-full flex flex-col items-center justify-center bg-gray-50 text-gray-600 gap-3">
    <Spinner size="lg" className="text-primary h-8 w-8" />
    <p className="text-sm">Loading…</p>
  </div>
);

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return fullPageSpinner;
  if (!user) return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  if (isEmailConfirmationLocked(user)) {
    return <Navigate to="/email-confirmation-required" replace />;
  }
  return children;
};

const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return fullPageSpinner;
  const resetJustCompleted =
    location.pathname === '/login' &&
    new URLSearchParams(location.search).get('password-reset') === 'success';
  if (user && !resetJustCompleted) {
    const redirect = new URLSearchParams(location.search).get('redirect');
    const safeRedirect =
      redirect && redirect.startsWith('/') && !redirect.startsWith('//') ? redirect : null;
    // Locked users must still reach /confirm-email?token=… after login.
    if (safeRedirect?.startsWith('/confirm-email')) {
      return <Navigate to={safeRedirect} replace />;
    }
    if (isEmailConfirmationLocked(user)) {
      return <Navigate to="/email-confirmation-required" replace />;
    }
    if (safeRedirect) {
      return <Navigate to={safeRedirect} replace />;
    }
    return <Navigate to="/dashboard" replace />;
  }
  return children;
};

function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="/accept-invite" element={<AcceptInvite />} />
      <Route path="/confirm-email" element={<ConfirmEmail />} />
      <Route path="/email-confirmation-required" element={<EmailConfirmationRequired />} />
      <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
      <Route path="/signup" element={<PublicRoute><Signup /></PublicRoute>} />
      <Route path="/forgot-password" element={<PublicRoute><ForgotPassword /></PublicRoute>} />
      {/* Public auth route (not PublicRoute): recovery establishes a session while a CRM profile may already exist, so do not redirect authenticated users away from this page. Add https://crm.prosperityleaders.net/reset-password to Supabase Auth redirect allowlist — see PASSWORD_RESET_REDIRECT_URL in src/lib/api.js */}
      <Route path="/reset-password" element={<ResetPassword />} />
      {/* Public auth route (not PublicRoute / ProtectedRoute): marketing-site SSO hash tokens must be confirmed as SIGNED_IN plus a resolved users profile before entering the app. */}
      <Route path="/sso-handoff" element={<SsoHandoff />} />
      {/* Public funnel pages — no auth, no CRM chrome */}
      <Route path="/go/:slug" element={<FunnelPage />} />
      <Route path="/go/:slug/:step" element={<FunnelPage />} />
      {/* Public agent CRM calendar booking — no auth, no CRM chrome */}
      <Route path="/book/:slug" element={<PublicBooking />} />
      {/* Public pre-underwriting intake — go.prosperityleaders.net/preapp/... */}
      <Route path="/preapp/:agencySlug" element={<PreUnderwritingForm />} />
      <Route path="/preapp/:agencySlug/:agentId" element={<PreUnderwritingForm />} />
      {/* Authenticated full-screen Launch Presentation tool — ProtectedRoute only, no AppLayout chrome (popup / Zoom screen-share surface). */}
      <Route
        path="/launch-toolkit/presentation"
        element={
          <ProtectedRoute>
            <LaunchPresentationTool />
          </ProtectedRoute>
        }
      />
      <Route element={<ProtectedRoute><AppLayout /></ProtectedRoute>}>
        <Route path="/dashboard" element={<HomeDashboard />} />
        <Route path="/academy-hub" element={<AcademyHub />} />
        <Route path="/launch-toolkit" element={<LaunchToolkit />} />
        <Route path="/top25" element={<Top25List />} />
        <Route path="/leads" element={<LeadsList />} />
        <Route path="/leads/new" element={<LeadForm />} />
        <Route path="/leads/:id" element={<LeadDetail />} />
        <Route path="/reminders" element={<Reminders />} />
        <Route path="/recruits" element={<LeadsList />} />
        <Route path="/pipeline" element={<Pipeline />} />
        <Route path="/tracker" element={<Tracker />} />
        <Route path="/inbox" element={<Inbox />} />
        <Route path="/calendar" element={<Calendar />} />
        <Route path="/funnels" element={<FunnelList />} />
        <Route
          path="/funnels/new"
          element={
            <RoleGuard allowedRoles={FUNNEL_WRITE_ROLES} fallback="/funnels">
              <FunnelNew />
            </RoleGuard>
          }
        />
        <Route path="/funnels/templates" element={<FunnelTemplates />} />
        <Route path="/funnels/:id/settings" element={<FunnelSettings />} />
        <Route path="/funnels/:id/submissions" element={<FunnelSubmissions />} />
        <Route path="/funnels/:id" element={<FunnelBuilder />} />
        <Route path="/mentors" element={<EventList />} />
        <Route path="/mentors/new" element={<EventEditor />} />
        <Route path="/mentors/:id/registrations" element={<EventRegistrations />} />
        <Route path="/mentors/:id" element={<EventEditor />} />
        <Route path="/workspace" element={<CallWorkspace />} />
        <Route path="/toolkit" element={<Toolkit />} />
        <Route path="/applications" element={<ApplicationsList />} />
        <Route path="/applications/new" element={<ApplicationForm />} />
        <Route path="/applications/reports" element={<ApplicationsReports />} />
        <Route path="/applications/pre-underwriting" element={<PreUnderwritingLogsList />} />
        <Route path="/applications/pre-underwriting/:id" element={<PreUnderwritingLogDetail />} />
        <Route path="/applications/:id" element={<ApplicationDetail />} />
        <Route path="/financial-analysis" element={<FinancialAnalysisList />} />
        <Route path="/financial-analysis/:id/report" element={<FinancialAnalysisReport />} />
        <Route path="/financial-analysis/:id" element={<FinancialAnalysisDetail />} />
        <Route
          path="/marketing-intelligence"
          element={
            <RoleGuard allowedRoles={['admin', 'super_admin']} fallback="/dashboard">
              <MarketingIntelligence />
            </RoleGuard>
          }
        />
        <Route
          path="/marketing-intelligence/new"
          element={
            <RoleGuard allowedRoles={['admin', 'super_admin']} fallback="/dashboard">
              <MarketingIntelligenceForm />
            </RoleGuard>
          }
        />
        <Route
          path="/marketing-intelligence/:id/edit"
          element={
            <RoleGuard allowedRoles={['admin', 'super_admin']} fallback="/dashboard">
              <MarketingIntelligenceForm />
            </RoleGuard>
          }
        />
        <Route path="/profile" element={<Profile />} />
        <Route path="/settings/*" element={<Settings />} />
        <Route
          path="/campaigns/templates"
          element={
            <RoleGuard allowedRoles={['admin', 'super_admin']}>
              <TemplateManager />
            </RoleGuard>
          }
        />
        <Route
          path="/campaigns/templates/new"
          element={
            <RoleGuard allowedRoles={['admin', 'super_admin']}>
              <TemplateEditor />
            </RoleGuard>
          }
        />
        <Route
          path="/campaigns/templates/:id/edit"
          element={
            <RoleGuard allowedRoles={['admin', 'super_admin']}>
              <TemplateEditor />
            </RoleGuard>
          }
        />
        <Route
          path="/campaigns/*"
          element={
            <RoleGuard allowedRoles={['admin', 'manager', 'super_admin']} fallback="/dashboard">
              <Campaigns />
            </RoleGuard>
          }
        />
        <Route path="/admin" element={
          <RoleGuard allowedRoles={['super_admin']} fallback="/dashboard">
            <Admin />
          </RoleGuard>
        } />
      </Route>
    </Routes>
  );
}

export default function App() {
  const isStudentHost =
    typeof window !== 'undefined' &&
    window.location.hostname.toLowerCase() === 'student.prosperityleaders.net';

  if (isStudentHost) {
    return <StudentApp />;
  }

  return (
    <AuthProvider>
      <SearchProvider>
        <ToastProvider>
          <BrowserRouter>
            <AppRoutes />
          </BrowserRouter>
        </ToastProvider>
      </SearchProvider>
    </AuthProvider>
  );
}